// src/pages/AdminDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';

import {
  Dashboard as DashboardIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('PENDING');
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const adminData = sessionStorage.getItem('adminUser');
    if (!adminData) {
      navigate('/admin/login');
      return;
    }

    setAdminUser(JSON.parse(adminData));
    fetchStats();
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('adminUser');
    navigate('/');
  };

  // Fetch analytics
  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const res = await fetch(
        `https://py.taih.ai/api/admin/ratings/stats?source=admin`
      );

      if (res.ok) {
        const data = await res.json();
        setStats(data);
        loadPendingList();
      } else {
        throw new Error();
      }
    } catch {
      setError('Failed to load analytics.');
    } finally {
      setLoadingStats(false);
    }
  };

  const loadPendingList = async () => {
    try {
      setActiveFilter('PENDING');
      setLoadingList(true);

      const res = await fetch(
        'https://py.taih.ai/api/admin/review/pending?page=1&page_size=25'
      );

      if (res.ok) {
        const data = await res.json();
        setList(data);
      } else {
        throw new Error();
      }
    } catch {
      setError('Failed to load pending assessments.');
    } finally {
      setLoadingList(false);
    }
  };

  const loadRatedList = async (rating) => {
    try {
      setActiveFilter(rating);
      setLoadingList(true);

      const res = await fetch(
        `https://py.taih.ai/api/admin/ratings/list?rating=${rating}&page=1&page_size=25&source=admin`
      );

      if (res.ok) {
        const data = await res.json();
        setList(data);
      } else {
        throw new Error();
      }
    } catch {
      setError('Failed to load assessments.');
    } finally {
      setLoadingList(false);
    }
  };

  if (!adminUser) return null;

  const analyticsConfig = stats
    ? [
        {
          label: 'Pending Review',
          value: stats.pending_review_total,
          color: '#ff9800', // warning orange
          onClick: () => loadPendingList(),
          key: 'PENDING'
        },
        {
          label: 'Leading',
          value: stats.counts.LEADING,
          color: '#004494', // theme blue
          onClick: () => loadRatedList('LEADING'),
          key: 'LEADING'
        },
        {
          label: 'Advanced',
          value: stats.counts.ADVANCED,
          color: '#4caf50', // success green
          onClick: () => loadRatedList('ADVANCED'),
          key: 'ADVANCED'
        },
        {
          label: 'Developing',
          value: stats.counts.DEVELOPING,
          color: '#004494', // theme blue
          onClick: () => loadRatedList('DEVELOPING'),
          key: 'DEVELOPING'
        },
        {
          label: 'Critical',
          value: stats.counts.CRITICAL,
          color: '#f44336', // error red
          onClick: () => loadRatedList('CRITICAL'),
          key: 'CRITICAL'
        }
      ]
    : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', fontFamily: 'Helvetica, Arial, sans-serif' }}>

      {/* HEADER */}
      <Box sx={{ bgcolor: '#004494', color: 'white', py: 2 }}>
        <Container>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DashboardIcon sx={{ mr: 1 }} />
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'white',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
              >
                Admin Dashboard
              </Typography>
            </Box>

            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ 
                color: 'white', 
                borderColor: 'white',
                fontFamily: 'Helvetica, Arial, sans-serif',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              variant="outlined"
              size="small"
            >
              Logout
            </Button>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            {error}
          </Alert>
        )}

        {/* ANALYTICS PANEL */}
        <Grid container justifyContent="center" sx={{ mb: 4 }}>
          <Grid item xs={12} md={10}>
            <Card sx={{ border: '2px solid #004494' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    textAlign: 'center', 
                    mb: 3,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 500
                  }}
                >
                  Assessment Ratings Overview
                </Typography>

                {loadingStats ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress sx={{ color: '#004494' }} />
                  </Box>
                ) : (
                  <Grid container spacing={3} justifyContent="center">
                    {analyticsConfig.map((item) => (
                      <Grid key={item.label} item xs={6} md={2.4} textAlign="center">
                        <Typography
                          onClick={item.onClick}
                          sx={{
                            fontSize: '2rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            '&:hover': { opacity: 0.7 },
                            color: item.color
                          }}
                        >
                          {item.value}
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
                        >
                          {item.label}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* LIST BELOW ANALYTICS */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            {activeFilter === 'PENDING'
              ? 'Pending Assessments'
              : `${activeFilter} Rated Assessments`}
          </Typography>

          {loadingList ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress sx={{ color: '#004494' }} />
              <Typography 
                sx={{ 
                  ml: 2,
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
              >
                Loading...
              </Typography>
            </Box>
          ) : list.length === 0 ? (
            <Typography sx={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
              No Assessments of this type
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: 'Helvetica, Arial, sans-serif'
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: 'Helvetica, Arial, sans-serif'
                      }}
                    >
                      Organization
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: 'Helvetica, Arial, sans-serif'
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: 'Helvetica, Arial, sans-serif'
                      }}
                    >
                      Stage
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: 'Helvetica, Arial, sans-serif'
                      }}
                    >
                      Email
                    </TableCell>

                    {activeFilter !== 'PENDING' && (
                      <>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            fontFamily: 'Helvetica, Arial, sans-serif'
                          }}
                        >
                          Score
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            fontFamily: 'Helvetica, Arial, sans-serif'
                          }}
                        >
                          Maturity
                        </TableCell>
                      </>
                    )}

                    {activeFilter === 'PENDING' && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: 'Helvetica, Arial, sans-serif'
                        }}
                      >
                        Action
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {list.map((item) => (
                    <TableRow key={item.assessment_id} hover>
                      <TableCell 
                        sx={{ 
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          fontWeight: 400,
                          fontSize: '14px'
                        }}
                      >
                        #{item.assessment_id}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          fontWeight: 400,
                          fontSize: '14px'
                        }}
                      >
                        {item.org_name}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.company_type} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            borderColor: '#004494',
                            color: '#004494',
                            fontSize: '12px',
                            fontWeight: 400
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.stage} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            backgroundColor: '#004494',
                            color: 'white',
                            borderColor: '#004494',
                            fontSize: '12px',
                            fontWeight: 400
                          }}
                        />
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          fontWeight: 400,
                          fontSize: '14px',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={item.contact_email || 'N/A'} // Show full email on hover
                      >
                        {item.contact_email || 'N/A'}
                      </TableCell>

                      {activeFilter !== 'PENDING' && (
                        <>
                          <TableCell 
                            sx={{ 
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              fontWeight: 400,
                              fontSize: '14px'
                            }}
                          >
                            {item.overall_score?.toFixed(1)}%
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.maturity_level}
                              sx={{ 
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontSize: '12px',
                                fontWeight: 400
                              }}
                              color={
                                {
                                  LEADING: 'info',
                                  ADVANCED: 'success',
                                  DEVELOPING: 'warning',
                                  CRITICAL: 'error'
                                }[item.maturity_level]
                              }
                            />
                          </TableCell>
                        </>
                      )}

                      {activeFilter === 'PENDING' && (
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              navigate(`/admin/review/${item.assessment_id}`)
                            }
                            sx={{
                              backgroundColor: '#004494',
                              color: 'white',
                              fontFamily: 'Helvetica, Arial, sans-serif',
                              fontSize: '12px',
                              fontWeight: 400,
                              '&:hover': {
                                backgroundColor: '#003366'
                              }
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>

              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </Box>
  );
}